import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { apiClient, auth, tenants, ApiError } from "~/lib/api";
import { useAuthStore } from "~/lib/auth-store";
import {
  rememberDesktopRequest,
  desktopRequest,
  clearDesktopRequest,
} from "~/lib/desktop-intent";
import { AuthShell, Field, inputCls, primaryBtn } from "./auth.sign-in";
export const Route = createFileRoute("/desktop/authorize")({
  validateSearch: (raw): { request?: string } => ({
    request:
      typeof raw.request === "string" && /^[0-9a-f-]{36}$/i.test(raw.request)
        ? raw.request
        : undefined,
  }),
  component: DesktopAuthorize,
});
function DesktopAuthorize() {
  const { request } = Route.useSearch();
  const store = useAuthStore();
  const [selected, setSelected] = useState(""),
    [name, setName] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [callback, setCallback] = useState("");
  const id = request || desktopRequest();
  useEffect(() => {
    if (request) rememberDesktopRequest(request);
  }, [request]);
  async function approve() {
    if (!id) return;
    setBusy(true);
    setError("");
    try {
      const result = await apiClient.post<{ callback_url: string }>(
        "/api/v1/desktop/auth/approve",
        { request_id: id, tenant_id: selected },
      );
      const url = new URL(result.callback_url);
      if (
        url.protocol !== "jotty:" ||
        url.hostname !== "auth" ||
        url.pathname !== "/callback"
      )
        throw new Error("Invalid desktop return");
      setCallback(url.href);
      clearDesktopRequest();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : "Could not connect Jotty. Start again from the desktop app.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function createWorkspace(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const tenant = await tenants.create({
        name,
        slug:
          name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "") +
          "-" +
          crypto.randomUUID().slice(0, 8),
      });
      await store.refresh();
      setSelected(tenant.id);
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : "Could not create workspace.",
      );
    } finally {
      setBusy(false);
    }
  }
  if (callback)
    return (
      <AuthShell
        title="You’re signed in."
        subtitle="Your browser may ask permission to open the desktop app."
      >
        <div className="rounded-[10px] bg-[color:var(--color-surface-2)] px-4 py-3.5 text-sm text-[color:var(--color-text-subtle)]">
          {store.user?.email}
          <br />
          {store.tenants.find((t) => t.id === selected)?.name}
        </div>
        <a className={primaryBtn + " block text-center"} href={callback}>
          Open Jotty
        </a>
        <p className="mt-4 text-sm">
          Didn’t open? Click Open Jotty again. If the link expires, restart
          sign-in from the desktop app.
        </p>
        <Link to="/">Continue to web notes</Link>
      </AuthShell>
    );
  if (!id)
    return (
      <AuthShell
        title="Start from Jotty for Mac"
        subtitle="Open the desktop app and choose Continue in browser to start a secure sign-in."
      />
    );
  if (store.status === "unknown" || store.status === "loading")
    return <AuthShell title="Checking your account…" />;
  if (store.status !== "authenticated")
    return (
      <AuthShell
        title="Connect Jotty for Mac"
        subtitle="Sign in or create your Jotty account to continue."
      >
        <Link to="/auth/sign-in" className={primaryBtn + " block text-center"}>
          Sign in
        </Link>
        <Link to="/auth/sign-up" className="mt-4 block text-center">
          Create an account
        </Link>
      </AuthShell>
    );
  return (
    <AuthShell
      title="Open your workspace in Jotty"
      subtitle={`Signed in as ${store.user?.email}`}
    >
      <button
        className="mb-5 text-sm underline"
        onClick={() => void auth.signOut().then(() => store.clear())}
      >
        Use another account
      </button>
      {store.tenants.length ? (
        <>
          <Field label="Workspace">
            <select
              className={inputCls}
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">Choose a workspace</option>
              {store.tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          <button
            disabled={!selected || busy}
            className={primaryBtn + " mt-5"}
            onClick={() => void approve()}
          >
            {busy ? "Connecting…" : "Continue to Jotty"}
          </button>
        </>
      ) : (
        <form onSubmit={createWorkspace}>
          <Field label="Workspace name">
            <input
              className={inputCls}
              required
              maxLength={100}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <button className={primaryBtn + " mt-5"} disabled={busy}>
            Create workspace
          </button>
        </form>
      )}
      <p className="mt-4 text-sm">
        You’re authorizing this Mac to access your Jotty account. Your Google
        Calendar connection is optional and happens later.
      </p>
      {error && (
        <p className="mt-4 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
    </AuthShell>
  );
}
